import { useQuery } from '@tanstack/react-query';
import { useRef, useState, type ReactElement } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import markUrl from '../../assets/21k-logo.svg';
import { ApiError, violationMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatInrMinor } from '../../lib/constants';
import { AppShell } from '../components/AppShell';
import { downloadReceiptPdf } from '../lib/download-receipt-pdf';
import { fetchPaymentReceipt } from '../lib/parent-portal-api';

type ReceiptLine = {
  description?: string;
  detail?: string | null;
  amountMinor?: string;
};

type ReceiptAdjustment = {
  label?: string;
  amountMinor?: string;
};

type ReceiptSchool = {
  legalName?: string;
  addressLines?: string[];
  cin?: string;
  gstn?: string;
};

/**
 * Official 21K fee receipt — rendered entirely from snapshot_json.
 * Layout matches the school FEE RECEIPT (original copy).
 */
export function ReceiptPage(): ReactElement {
  const { paymentId = '' } = useParams();
  const { token, loading } = useAuth();
  const receiptRef = useRef<HTMLElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const receiptQuery = useQuery({
    queryKey: ['payment-receipt', paymentId],
    queryFn: () => fetchPaymentReceipt(paymentId),
    enabled: Boolean(token && paymentId),
  });

  if (!loading && !token) return <Navigate to="/login" replace />;

  const snapshot = receiptQuery.data?.snapshotJson ?? {};
  const lineItems = Array.isArray(snapshot.lineItems) ? snapshot.lineItems as ReceiptLine[] : [];
  const adjustments = Array.isArray(snapshot.adjustments)
    ? snapshot.adjustments as ReceiptAdjustment[]
    : [{ label: 'One time payment incentive', amountMinor: '0' }];
  const school = (snapshot.school ?? {}) as ReceiptSchool;
  const receiptNumber = receiptQuery.data?.receiptNumber ?? String(snapshot.orderNumber ?? '—');
  const transactionId = String(snapshot.paymentReference ?? '—');
  const transactionDate = String(
    snapshot.transactionDate
    ?? formatReceiptDate(String(snapshot.capturedAt ?? '')),
  );
  const subtotalMinor = String(
    snapshot.subtotalMinor
    ?? sumMinor(lineItems.map((line) => line.amountMinor)),
  );
  const totalMinor = String(snapshot.amountMinor ?? subtotalMinor);

  return (
    <AppShell title="Receipt" width="wide">
      <div className="receipt-page">
      <div className="receipt-toolbar no-print">
        <Link className="btn btn-ghost" to="/payments">← Back to payments</Link>
        <button
          className="btn btn-secondary"
          type="button"
          disabled={!receiptQuery.data || downloading}
          onClick={() => {
            const sheet = receiptRef.current;
            if (!sheet) return;
            setDownloadError(null);
            setDownloading(true);
            void downloadReceiptPdf(sheet, receiptNumber)
              .catch(() => setDownloadError('Could not download the PDF. Please try again.'))
              .finally(() => setDownloading(false));
          }}
        >
          {downloading ? 'Preparing PDF…' : 'Download PDF'}
        </button>
      </div>

      {receiptQuery.error ? (
        <div className="error-banner" role="alert">
          {receiptQuery.error instanceof ApiError
            ? violationMessage(receiptQuery.error.problem)
            : 'Could not load receipt'}
        </div>
      ) : null}

      {downloadError ? (
        <div className="error-banner" role="alert">{downloadError}</div>
      ) : null}

      {receiptQuery.isLoading ? <p className="text-muted">Loading receipt…</p> : null}

      {receiptQuery.data ? (
        <article ref={receiptRef} className="fee-receipt" aria-label="Fee receipt">
          <header className="fee-receipt-letterhead">
            <div className="fee-receipt-brand">
              <img className="fee-receipt-logo" src={markUrl} alt="21K School" />
            </div>
            <div className="fee-receipt-issuer">
              <p className="fee-receipt-legal-name">
                {school.legalName ?? '21K School Education Private Limited'}
              </p>
              {(school.addressLines ?? DEFAULT_ADDRESS).map((line) => (
                <p key={line}>{line}</p>
              ))}
              <p className="fee-receipt-reg">CIN: {school.cin ?? 'U72900KA2020PTC133164'}</p>
              <p className="fee-receipt-reg">GSTN: {school.gstn ?? '29AANCM3660L1ZU'}</p>
            </div>
          </header>

          <p className="fee-receipt-title" role="heading" aria-level={1}>FEE RECEIPT</p>
          <p className="fee-receipt-copy">Original Copy</p>

          <dl className="fee-receipt-meta">
            <div>
              <dt>Receipt No</dt>
              <dd>{receiptNumber}</dd>
            </div>
            <div>
              <dt>Transaction Id</dt>
              <dd>{transactionId}</dd>
            </div>
            <div>
              <dt>Parent Name</dt>
              <dd>{String(snapshot.parentName ?? '—')}</dd>
            </div>
            <div>
              <dt>Transaction Date</dt>
              <dd>{transactionDate || '—'}</dd>
            </div>
            <div className="fee-receipt-meta-wide">
              <dt>Mode Of Payment</dt>
              <dd>{String(snapshot.modeOfPayment ?? 'Paid via Razorpay')}</dd>
            </div>
          </dl>

          <table className="fee-receipt-student">
            <thead>
              <tr>
                <th scope="col">Student Name</th>
                <th scope="col">Enrollment No.</th>
                <th scope="col">Grade</th>
                <th scope="col">Academic Year</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{String(snapshot.childName ?? '—')}</td>
                <td>{String(snapshot.enrolmentNumber ?? '—')}</td>
                <td>{String(snapshot.gradeLabel ?? '—')}</td>
                <td>{String(snapshot.academicYearCode ?? '—')}</td>
              </tr>
            </tbody>
          </table>

          <table className="fee-receipt-lines">
            <thead>
              <tr>
                <th scope="col" className="fee-receipt-slno">SLNO</th>
                <th scope="col">Particulars</th>
                <th scope="col" className="fee-receipt-amt">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.length > 0 ? lineItems.map((line, index) => (
                <tr key={`${line.description ?? 'line'}-${index}`}>
                  <td className="fee-receipt-slno">{index + 1}</td>
                  <td>
                    <span className="fee-receipt-particular">{line.description ?? 'Fee'}</span>
                    {line.detail ? <span className="fee-receipt-detail">{line.detail}</span> : null}
                  </td>
                  <td className="fee-receipt-amt">{formatReceiptAmount(line.amountMinor)}</td>
                </tr>
              )) : (
                <tr>
                  <td className="fee-receipt-slno">1</td>
                  <td>
                    <span className="fee-receipt-particular">
                      {String(snapshot.purposeLabel ?? snapshot.purpose ?? 'Fee')}
                    </span>
                  </td>
                  <td className="fee-receipt-amt">{formatReceiptAmount(totalMinor)}</td>
                </tr>
              )}
            </tbody>
          </table>

          <table className="fee-receipt-totals">
            <tbody>
              <tr>
                <th scope="row">Sub Total</th>
                <td>{formatReceiptAmount(subtotalMinor)}</td>
              </tr>
              {adjustments.map((row, index) => (
                <tr key={`${row.label ?? 'adjustment'}-${index}`}>
                  <th scope="row" className="fee-receipt-incentive">{row.label ?? 'One time payment incentive'}</th>
                  <td>{formatAdjustmentAmount(row.amountMinor)}</td>
                </tr>
              ))}
              <tr className="fee-receipt-grand">
                <th scope="row">
                  Total
                  {snapshot.amountInWords ? (
                    <span className="fee-receipt-words">{String(snapshot.amountInWords)}</span>
                  ) : null}
                </th>
                <td>{formatReceiptAmount(totalMinor)}</td>
              </tr>
            </tbody>
          </table>

          <p className="fee-receipt-note">
            <strong>Note:</strong> {String(snapshot.generationNote ?? 'Computer generated, signature not required.')}
          </p>
        </article>
      ) : null}
      </div>
    </AppShell>
  );
}

const DEFAULT_ADDRESS = [
  'Regd. Office: 541 5th Main Road 16th Cross Road 2nd Stage',
  'Indiranagar, Bengaluru 560038',
  'Karnataka, India',
];

function formatReceiptDate(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}

function formatReceiptAmount(amountMinor?: string): string {
  if (!amountMinor) return '₹ 0.00';
  return formatInrMinor(amountMinor).replace('₹', '₹ ');
}

function formatAdjustmentAmount(amountMinor?: string): string {
  const value = amountMinor ?? '0';
  const absolute = value.startsWith('-') ? value.slice(1) : value;
  return `- ${formatReceiptAmount(absolute)}`;
}

function sumMinor(values: Array<string | undefined>): string {
  let total = 0n;
  for (const value of values) {
    if (!value) continue;
    try {
      total += BigInt(value);
    } catch {
      /* ignore non-integer snapshot values */
    }
  }
  return total.toString();
}
